(function () {
  const ROOT_SELECTOR = '[data-ts-brand-mobile-map]';
  const LIST_CARD_SELECTOR = '[data-ts-brand-model-card]';
  const MARKET_ITEMS_SELECTOR = '[data-ts-brand-market-items]';
  const MARKET_CARD_SELECTOR = '.ts-brand-tabs__market-card[data-ts-model]';
  const POPULAR_CARD_SELECTOR = '#ts-popular .ts-brand-popular__card';
  const TRACKING_EVENT = 'timeseek:tracking';
  const MAX_MOBILE_WIDTH = 768;
  const PRICE_TICK_CANDIDATES = [
    100000,
    300000,
    500000,
    1000000,
    3000000,
    5000000,
    10000000,
    30000000,
    50000000,
    100000000,
    300000000,
  ];
  const INVENTORY_STEP_CANDIDATES = [25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000];
  const BUBBLE_COLORS = [
    '#a9baf7',
    '#9be7f6',
    '#d7b7f4',
    '#c7c2ff',
    '#b0f0c2',
    '#ffd0c6',
    '#fde392',
    '#cdd7ff',
    '#c6f3fe',
    '#f5c3f2',
    '#c7d0ff',
    '#ffb8be',
  ];
  const controllers = new Map();

  function normalizeString(value) {
    return String(value ?? '')
      .normalize('NFKC')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeMatchKey(value) {
    return normalizeString(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\u3040-\u30ff\u4e00-\u9faf]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function compactMatchKey(value) {
    return normalizeMatchKey(value).replace(/\s+/g, '');
  }

  function parseJson(text, fallbackValue) {
    try {
      return JSON.parse(text);
    } catch (_error) {
      return fallbackValue;
    }
  }

  function ensureArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function dedupeMarketItems(items) {
    const seen = new Set();
    const deduped = [];

    ensureArray(items).forEach((item) => {
      const modelName = normalizeString(item?.model_name || item?.modelName || item?.model || item?.name);
      const price = parseNumber(item?.price);
      if (!modelName || !Number.isFinite(price)) {
        return;
      }

      const cacheKey = `${compactMatchKey(modelName)}::${Math.round(price)}`;
      if (seen.has(cacheKey)) {
        return;
      }

      seen.add(cacheKey);
      deduped.push({
        model_name: modelName,
        price: Math.round(price),
      });
    });

    return deduped;
  }

  function parseNumber(value) {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : NaN;
    }

    const normalized = normalizeString(value).replace(/[^\d.-]/g, '');
    if (!normalized) {
      return NaN;
    }

    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? numericValue : NaN;
  }

  function parseInteger(value) {
    const numericValue = parseNumber(value);
    return Number.isFinite(numericValue) ? Math.round(numericValue) : NaN;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function midpoint(min, max) {
    if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) {
      return NaN;
    }
    return min + ((max - min) / 2);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function isMobileViewport() {
    return window.innerWidth <= MAX_MOBILE_WIDTH;
  }

  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function pushMetric(eventName, payload) {
    const safePayload = payload || {};
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: eventName, ...safePayload });
    }

    document.dispatchEvent(
      new CustomEvent(TRACKING_EVENT, {
        detail: {
          event: eventName,
          payload: safePayload,
        },
      })
    );
  }

  function parsePriceRangeText(text) {
    const normalized = normalizeString(text)
      .replace(/￥/g, '¥')
      .replace(/\s+/g, ' ');

    const parseToken = (rawToken) => {
      const token = normalizeString(rawToken).replace(/,/g, '');
      const numericMatch = token.match(/\d+(?:\.\d+)?/);
      if (!numericMatch) {
        return NaN;
      }
      const numericValue = Number(numericMatch[0]);
      if (!Number.isFinite(numericValue)) {
        return NaN;
      }
      if (token.includes('万')) {
        return Math.round(numericValue * 10000);
      }
      return Math.round(numericValue);
    };

    const rangeTokens = normalized
      .split(/[〜~～]/)
      .map((token) => normalizeString(token))
      .filter(Boolean);

    if (rangeTokens.length >= 2) {
      return {
        priceMin: parseToken(rangeTokens[0]),
        priceMax: parseToken(rangeTokens[1]),
      };
    }

    const matches = normalized.match(/\d[\d,.]*(?:\s*万)?/g);
    if (!matches || matches.length < 2) {
      return {
        priceMin: NaN,
        priceMax: NaN,
      };
    }

    return {
      priceMin: parseToken(matches[0]),
      priceMax: parseToken(matches[1]),
    };
  }

  function formatInteger(value) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? new Intl.NumberFormat('ja-JP').format(Math.round(numericValue))
      : '-';
  }

  function formatAxisTickYen(value) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return '';
    }
    return `${new Intl.NumberFormat('ja-JP').format(Math.round(numericValue / 10000))}万`;
  }

  function formatManYen(value) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return '-';
    }
    const manValue = numericValue / 10000;
    const fractionDigits = manValue >= 100 ? 0 : 1;
    return `${new Intl.NumberFormat('ja-JP', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(manValue)}万円`;
  }

  function formatPriceRange(model) {
    if (Number.isFinite(model.priceMin) && Number.isFinite(model.priceMax)) {
      return `${formatManYen(model.priceMin)}〜${formatManYen(model.priceMax)}`;
    }
    if (normalizeString(model.priceRangeText)) {
      return normalizeString(model.priceRangeText);
    }
    if (Number.isFinite(model.centralPrice)) {
      return formatManYen(model.centralPrice);
    }
    return '価格情報なし';
  }

  function buildModelId(raw, brandHandle, fallbackIndex) {
    const directHandle = normalizeString(raw.collection_handle || raw.collectionHandle || raw.handle);
    if (directHandle) {
      return directHandle;
    }

    const directSlug = normalizeString(raw.model_slug || raw.modelSlug);
    if (directSlug) {
      return brandHandle ? `model-${brandHandle.replace(/^brand-/, '')}-${directSlug}` : directSlug;
    }

    const href = normalizeString(raw.canonical_url || raw.canonicalUrl || raw.url);
    if (href.includes('/collections/')) {
      return href.split('/collections/').pop().split(/[?#]/)[0];
    }

    return `${brandHandle || 'brand'}-model-${fallbackIndex + 1}`;
  }

  function normalizeModel(raw, brandHandle, index) {
    const inventory = parseInteger(
      raw.total_inventory_count ??
      raw.market_inventory_count ??
      raw.inventory_count ??
      raw.inventory
    );

    let priceMin = parseNumber(raw.ref_price_min ?? raw.price_min);
    let priceMax = parseNumber(raw.ref_price_max ?? raw.price_max);

    if (!Number.isFinite(priceMin) || !Number.isFinite(priceMax)) {
      const parsedRange = parsePriceRangeText(raw.price_range_text || raw.ref_price_range_text || raw.ref_price_range);
      if (!Number.isFinite(priceMin)) {
        priceMin = parsedRange.priceMin;
      }
      if (!Number.isFinite(priceMax)) {
        priceMax = parsedRange.priceMax;
      }
    }

    const centralPrice = Number.isFinite(parseNumber(raw.price_median))
      ? parseNumber(raw.price_median)
      : midpoint(priceMin, priceMax);

    const href = normalizeString(raw.canonical_url || raw.canonicalUrl || raw.url);
    const name = normalizeString(
      raw.model_name_ja ||
      raw.model_name ||
      raw.model_family ||
      raw.title ||
      raw.name
    );

    if (!name || !Number.isFinite(inventory) || inventory <= 0 || !Number.isFinite(centralPrice)) {
      return null;
    }

    const modelId = buildModelId(raw, brandHandle, index);
    const pageStatus = normalizeString(raw.page_status || raw.pageStatus);
    const isPageAvailableValue = raw.is_page_available ?? raw.isPageAvailable;
    const isPageAvailable =
      isPageAvailableValue === true ||
      isPageAvailableValue === 'true' ||
      isPageAvailableValue === 1 ||
      isPageAvailableValue === '1' ||
      (!pageStatus || pageStatus === 'public');

    return {
      id: modelId,
      name,
      shortLabel: normalizeString(raw.shortLabel || raw.short_label || raw.model_label || raw.model_family || name),
      inventory,
      priceMin,
      priceMax,
      centralPrice,
      href,
      priceRangeText: normalizeString(raw.price_range_text || raw.ref_price_range_text || raw.ref_price_range),
      available: Boolean(href) && isPageAvailable,
      color: BUBBLE_COLORS[index % BUBBLE_COLORS.length],
    };
  }

  function normalizeCardModel(card, brandHandle, index) {
    if (!card) {
      return null;
    }

    const title = normalizeString(card.dataset.modelName || card.querySelector('.ts-brand-popular__title')?.textContent);
    const raw = {
      collection_handle: normalizeString(card.dataset.modelId),
      model_name_ja: title,
      canonical_url: normalizeString(card.dataset.modelUrl || card.getAttribute('href')),
      is_page_available: normalizeString(card.dataset.available),
      total_inventory_count: normalizeString(card.dataset.tsModelInventory),
      ref_price_min: normalizeString(card.dataset.tsModelPriceMin),
      ref_price_max: normalizeString(card.dataset.tsModelPriceMax),
      price_range_text: normalizeString(card.dataset.tsModelPriceRangeText),
    };

    return normalizeModel(raw, brandHandle, index);
  }

  function collectCardModels(brandHandle) {
    return Array.from(document.querySelectorAll(LIST_CARD_SELECTOR))
      .filter((card) => normalizeString(card.dataset.brandHandle) === brandHandle)
      .map((card, index) => normalizeCardModel(card, brandHandle, index))
      .filter(Boolean);
  }

  function collectMarketItemsFromDom() {
    return dedupeMarketItems(
      Array.from(document.querySelectorAll(MARKET_CARD_SELECTOR)).map((card) => ({
        model_name: normalizeString(card.dataset.tsModel || card.getAttribute?.('data-ts-model')),
        price: parseNumber(card.dataset.tsPrice || card.getAttribute?.('data-ts-price')),
      }))
    );
  }

  function mergeModels(primaryModels, fallbackModels) {
    const merged = new Map();

    primaryModels.forEach((model) => {
      if (model?.id) {
        merged.set(model.id, model);
      }
    });

    fallbackModels.forEach((model) => {
      if (!model?.id) {
        return;
      }

      const existing = merged.get(model.id);
      if (!existing) {
        merged.set(model.id, model);
        return;
      }

      merged.set(model.id, {
        ...existing,
        href: existing.href || model.href,
        available: existing.available || model.available,
        inventory: Number.isFinite(existing.inventory) && existing.inventory > 0 ? existing.inventory : model.inventory,
        priceMin: Number.isFinite(existing.priceMin) ? existing.priceMin : model.priceMin,
        priceMax: Number.isFinite(existing.priceMax) ? existing.priceMax : model.priceMax,
        centralPrice: Number.isFinite(existing.centralPrice) ? existing.centralPrice : model.centralPrice,
        priceRangeText: existing.priceRangeText || model.priceRangeText,
        inventoryLabel: existing.inventoryLabel || model.inventoryLabel,
        aliases: ensureArray(existing.aliases).length ? existing.aliases : model.aliases,
      });
    });

    return Array.from(merged.values());
  }

  function extractHandleTail(handle, brandHandle) {
    const normalizedHandle = normalizeString(handle);
    if (!normalizedHandle) {
      return '';
    }

    const brandSlug = normalizeString(brandHandle).replace(/^brand-/, '');
    const prefixed = `model-${brandSlug}-`;
    if (brandSlug && normalizedHandle.startsWith(prefixed)) {
      return normalizedHandle.slice(prefixed.length);
    }

    if (normalizedHandle.startsWith('model-')) {
      const body = normalizedHandle.slice(6);
      const segments = body.split('-');
      if (segments.length > 1) {
        return segments.slice(1).join('-');
      }
      return body;
    }

    return normalizedHandle;
  }

  function buildModelAliases(raw, brandHandle, fallbackIndex) {
    const aliases = new Set();
    const pushAlias = (value) => {
      const normalizedValue = normalizeString(value);
      if (!normalizedValue) {
        return;
      }
      aliases.add(normalizedValue);
      const normalizedKey = normalizeMatchKey(normalizedValue);
      if (normalizedKey && normalizedKey !== normalizedValue) {
        aliases.add(normalizedKey);
      }
    };

    const modelId = buildModelId(raw, brandHandle, fallbackIndex);
    const handleTail = extractHandleTail(modelId, brandHandle);
    const aliasProbe = [
      raw.model_name_ja,
      raw.model_name,
      raw.model_family,
      raw.title,
      raw.short_label,
      raw.shortLabel,
      handleTail,
      handleTail.replace(/-/g, ' '),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    [
      raw.model_name_ja,
      raw.model_name,
      raw.model_family,
      raw.title,
      raw.short_label,
      raw.shortLabel,
      handleTail,
      handleTail.replace(/-/g, ' '),
      modelId,
    ].forEach(pushAlias);

    if (handleTail) {
      pushAlias(handleTail.replace(/-/g, ''));
    }

    if (/(gmtmaster2|gmt master ii|gmt master 2|gmt masterii)/.test(aliasProbe)) {
      ['GMT Master II', 'GMT Master 2', 'GMT-Master II', 'GMT-MASTER II', 'GMTマスターII', 'GMTマスター2'].forEach(pushAlias);
    } else if (/(gmtmaster|gmt master)/.test(aliasProbe)) {
      ['GMT Master', 'GMT Master I', 'GMT-Master', 'GMT-Master I', 'GMTマスター'].forEach(pushAlias);
    }

    if (/(explorer-ii|explorer ii|explorer2)/.test(aliasProbe)) {
      ['Explorer II', 'Explorer 2', 'ExplorerII', 'エクスプローラーII'].forEach(pushAlias);
    } else if (/explorer/.test(aliasProbe)) {
      ['Explorer', 'Explorer I', 'Explorer 1', 'エクスプローラー'].forEach(pushAlias);
    }

    if (/(submarinerdate|submariner date)/.test(aliasProbe)) {
      ['Submariner Date', 'Submariner Date 41', 'サブマリーナデイト'].forEach(pushAlias);
    }

    if (/(submarinernodate|submariner no date|submariner no-date)/.test(aliasProbe)) {
      ['Submariner No Date', 'Submariner No-Date', 'サブマリーナ ノンデイト'].forEach(pushAlias);
    }

    if (/(cosmographdaytona|daytona)/.test(aliasProbe)) {
      ['Daytona', 'Cosmograph Daytona', 'コスモグラフデイトナ'].forEach(pushAlias);
    }

    if (/(sky-dweller|skydweller)/.test(aliasProbe)) {
      ['Sky-Dweller', 'Sky Dweller', 'スカイドゥエラー'].forEach(pushAlias);
    }

    if (/1908/.test(aliasProbe)) {
      ['1908', 'Perpetual 1908', 'パーペチュアル1908'].forEach(pushAlias);
    }

    if (/(ballonbleu|ballon bleu)/.test(aliasProbe)) {
      ['Ballon Bleu', 'Ballon Bleu de Cartier', 'バロン ブルー ドゥ カルティエ'].forEach(pushAlias);
    }

    if (/pasha/.test(aliasProbe)) {
      ['Pasha', 'Pasha de Cartier', 'Pasha C de Cartier'].forEach(pushAlias);
    }

    if (/(tankmust|tank must)/.test(aliasProbe)) {
      ['Tank Must', 'Must Tank', 'Must de Cartier Tank', 'タンク マスト'].forEach(pushAlias);
    }

    if (/(francaise|française)/.test(aliasProbe)) {
      ['Tank Francaise', 'Tank Française', 'タンク フランセーズ'].forEach(pushAlias);
    }

    if (/(americaine|américaine)/.test(aliasProbe)) {
      ['Tank Americaine', 'Tank Américaine', 'タンク アメリカン'].forEach(pushAlias);
    }

    if (/(panthere|panthère)/.test(aliasProbe)) {
      ['Panthere de Cartier', 'Panthère de Cartier', 'パンテール ドゥ カルティエ'].forEach(pushAlias);
    }

    if (/(louis cartier)/.test(aliasProbe)) {
      ['Tank Louis Cartier', 'タンク ルイ カルティエ'].forEach(pushAlias);
    }

    return Array.from(aliases).filter(Boolean);
  }

  function buildModelCandidates(rawModels, brandHandle) {
    return ensureArray(rawModels).map((rawModel, index) => {
      const id = buildModelId(rawModel, brandHandle, index);
      const href = normalizeString(rawModel.canonical_url || rawModel.canonicalUrl || rawModel.url);
      const aliases = buildModelAliases(rawModel, brandHandle, index);
      const aliasProbe = aliases.join(' ').toLowerCase();
      return {
        id,
        href,
        name: normalizeString(rawModel.model_name_ja || rawModel.model_name || rawModel.model_family || rawModel.title),
        rawModel,
        aliases,
        aliasKeys: aliases.map((alias) => ({
          key: normalizeMatchKey(alias),
          compact: compactMatchKey(alias),
        })),
        gmtPlain: /(gmt master i|gmt master|gmt-master i|gmt-master|gmtマスター)/.test(aliasProbe) && !/(gmt master ii|gmt master 2|gmtmaster2)/.test(aliasProbe),
        gmtSecond: /(gmt master ii|gmt master 2|gmtmaster2|gmt-master ii|gmtマスターii|gmtマスター2)/.test(aliasProbe),
        explorerPlain: /(explorer i|explorer 1|explorer)/.test(aliasProbe) && !/(explorer ii|explorer 2|explorerii)/.test(aliasProbe),
        explorerSecond: /(explorer ii|explorer 2|explorerii)/.test(aliasProbe),
      };
    });
  }

  function scoreMarketCandidate(nameKey, compactName, candidate) {
    let score = 0;

    candidate.aliasKeys.forEach((alias) => {
      if (!alias.key) {
        return;
      }

      if (nameKey === alias.key || compactName === alias.compact) {
        score = Math.max(score, 120);
        return;
      }

      if (
        nameKey.startsWith(alias.key) ||
        nameKey.endsWith(alias.key) ||
        alias.key.startsWith(nameKey) ||
        compactName.startsWith(alias.compact) ||
        compactName.endsWith(alias.compact)
      ) {
        score = Math.max(score, 96);
        return;
      }

      if (
        nameKey.includes(alias.key) ||
        alias.key.includes(nameKey) ||
        compactName.includes(alias.compact) ||
        alias.compact.includes(compactName)
      ) {
        score = Math.max(score, 76);
      }
    });

    const containsSecondSeriesToken =
      /\bii\b|\b2\b/.test(nameKey) ||
      compactName.includes('ii') ||
      compactName.includes('2');

    if (candidate.gmtPlain && containsSecondSeriesToken) {
      score -= 90;
    }
    if (candidate.gmtSecond && !containsSecondSeriesToken) {
      score -= 60;
    }
    if (candidate.explorerPlain && containsSecondSeriesToken) {
      score -= 90;
    }
    if (candidate.explorerSecond && !containsSecondSeriesToken) {
      score -= 60;
    }

    return score;
  }

  function deriveMarketModels(rawModels, marketItems, brandHandle) {
    const candidates = buildModelCandidates(rawModels, brandHandle);
    if (!candidates.length) {
      return [];
    }

    const statsById = new Map();

    ensureArray(marketItems).forEach((item) => {
      const modelName = normalizeString(item.model_name || item.modelName);
      const price = parseNumber(item.price);
      if (!modelName || !Number.isFinite(price)) {
        return;
      }

      const nameKey = normalizeMatchKey(modelName);
      const compactName = compactMatchKey(modelName);
      let bestCandidate = null;
      let bestScore = 0;

      candidates.forEach((candidate) => {
        const score = scoreMarketCandidate(nameKey, compactName, candidate);
        if (score > bestScore) {
          bestCandidate = candidate;
          bestScore = score;
        }
      });

      if (!bestCandidate || bestScore < 70) {
        return;
      }

      const existing = statsById.get(bestCandidate.id) || {
        inventory: 0,
        priceMin: Number.POSITIVE_INFINITY,
        priceMax: Number.NEGATIVE_INFINITY,
        candidate: bestCandidate,
      };

      existing.inventory += 1;
      existing.priceMin = Math.min(existing.priceMin, price);
      existing.priceMax = Math.max(existing.priceMax, price);
      statsById.set(bestCandidate.id, existing);
    });

    return Array.from(statsById.values())
      .sort((left, right) => right.inventory - left.inventory)
      .map((entry, index) => normalizeModel({
        collection_handle: entry.candidate.id,
        model_name_ja: entry.candidate.name,
        canonical_url: entry.candidate.href,
        is_page_available: entry.candidate.href && entry.candidate.href.includes('/collections/') ? 'true' : entry.candidate.rawModel.is_page_available,
        total_inventory_count: entry.inventory,
        ref_price_min: entry.priceMin,
        ref_price_max: entry.priceMax,
      }, brandHandle, index))
      .filter(Boolean)
      .map((model) => ({
        ...model,
        inventoryLabel: '掲載',
        aliases: candidates.find((candidate) => candidate.id === model.id)?.aliases || [],
      }));
  }

  function findBestModelForCard(card, models) {
    const rawModelId = normalizeString(card.dataset.modelId);
    if (rawModelId) {
      const exactMatch = models.find((model) => model.id === rawModelId);
      if (exactMatch) {
        return exactMatch;
      }
    }

    const title = normalizeString(card.dataset.modelName || card.querySelector('.ts-brand-popular__title')?.textContent);
    const href = normalizeString(card.dataset.modelUrl || card.getAttribute('href'));
    const titleKey = normalizeMatchKey(title);
    const hrefHandle = href.includes('/collections/')
      ? href.split('/collections/').pop().split(/[?#]/)[0]
      : '';

    let bestModel = null;
    let bestScore = 0;

    models.forEach((model) => {
      let score = 0;
      if (hrefHandle && model.id === hrefHandle) {
        score = 140;
      } else if (titleKey) {
        const aliases = ensureArray(model.aliases).length ? model.aliases : [model.name, model.shortLabel, model.id];
        aliases.forEach((alias) => {
          const aliasKey = normalizeMatchKey(alias);
          const aliasCompact = compactMatchKey(alias);
          if (!aliasKey) {
            return;
          }
          if (titleKey === aliasKey || compactMatchKey(title) === aliasCompact) {
            score = Math.max(score, 120);
          } else if (
            titleKey.includes(aliasKey) ||
            aliasKey.includes(titleKey) ||
            compactMatchKey(title).includes(aliasCompact) ||
            aliasCompact.includes(compactMatchKey(title))
          ) {
            score = Math.max(score, 80);
          }
        });
      }

      if (score > bestScore) {
        bestModel = model;
        bestScore = score;
      }
    });

    return bestScore >= 80 ? bestModel : null;
  }

  function ensureCardLink(card, href) {
    if (!card || !href) {
      return card;
    }

    if (String(card.tagName || '').toUpperCase() === 'A') {
      card.setAttribute('href', href);
      return card;
    }

    if (typeof document?.createElement !== 'function') {
      return card;
    }

    const nextCard = document.createElement('a');
    if (card.classList?.tokens) {
      nextCard.classList = card.classList;
    } else {
      nextCard.className = card.className || '';
    }

    if (card.attributes?.forEach) {
      card.attributes.forEach((value, name) => {
        nextCard.setAttribute(name, value);
      });
    } else if (Array.isArray(card.attributes)) {
      card.attributes.forEach((attribute) => {
        nextCard.setAttribute(attribute.name, attribute.value);
      });
    }

    nextCard.setAttribute('href', href);
    Object.entries(card.dataset || {}).forEach(([key, value]) => {
      nextCard.dataset[key] = value;
    });

    if ('firstChild' in card) {
      while (card.firstChild) {
        nextCard.appendChild(card.firstChild);
      }
    } else {
      while (card.children?.length) {
        const child = card.children.shift();
        nextCard.appendChild(child);
      }
    }

    if (typeof card.replaceWith === 'function') {
      card.replaceWith(nextCard);
      return nextCard;
    }

    return card;
  }

  function updateCardMeta(card, model) {
    const cardBody = card.querySelector('.ts-brand-popular__body');
    if (!cardBody) {
      return;
    }

    const metaNodes = Array.from(cardBody.querySelectorAll('.ts-brand-popular__meta'));
    const inventoryMeta = metaNodes[0];
    const inventoryLabel = model.inventoryLabel || '掲載';
    if (inventoryMeta && Number.isFinite(model.inventory) && model.inventory > 0) {
      inventoryMeta.innerHTML = inventoryLabel === '総合在庫'
        ? `<strong>総合在庫 ${formatInteger(model.inventory)}本</strong>`
        : `<strong>掲載 ${formatInteger(model.inventory)}件</strong>`;
    }

    const priceLabel = formatPriceRange(model);
    if (priceLabel === '価格情報なし') {
      return;
    }

    let priceMeta = metaNodes.find((node) => normalizeString(node.textContent).includes('参考価格'));
    if (!priceMeta) {
      priceMeta = document.createElement('p');
      priceMeta.className = 'ts-brand-popular__meta';
      cardBody.appendChild(priceMeta);
    }

    priceMeta.innerHTML = `参考価格: <span class="ts-brand-popular__price">${escapeHtml(priceLabel)}</span>`;
  }

  function hydratePopularCards(brandHandle, models) {
    const cards = Array.from(document.querySelectorAll(POPULAR_CARD_SELECTOR));

    cards.forEach((originalCard) => {
      const matchedModel = findBestModelForCard(originalCard, models);
      if (!matchedModel) {
        return;
      }

      let card = originalCard;
      if (matchedModel.href) {
        card = ensureCardLink(card, matchedModel.href);
      }

      card.setAttribute('data-ts-brand-model-card', '');
      card.dataset.brandHandle = brandHandle;
      card.dataset.modelId = matchedModel.id;
      card.dataset.modelName = matchedModel.name;
      if (matchedModel.href) {
        card.dataset.modelUrl = matchedModel.href;
        card.dataset.available = 'true';
      }
      if (Number.isFinite(matchedModel.inventory)) {
        card.dataset.tsModelInventory = String(matchedModel.inventory);
      }
      if (Number.isFinite(matchedModel.priceMin)) {
        card.dataset.tsModelPriceMin = String(matchedModel.priceMin);
      }
      if (Number.isFinite(matchedModel.priceMax)) {
        card.dataset.tsModelPriceMax = String(matchedModel.priceMax);
      }
      if (matchedModel.priceRangeText || Number.isFinite(matchedModel.priceMin) || Number.isFinite(matchedModel.priceMax)) {
        card.dataset.tsModelPriceRangeText = matchedModel.priceRangeText || formatPriceRange(matchedModel);
      }

      updateCardMeta(card, matchedModel);
    });
  }

  function pickPriceScale(values) {
    if (!values.length) {
      return {
        min: 300000,
        max: 30000000,
        ticks: [300000, 1000000, 3000000, 10000000, 30000000],
      };
    }

    const minValue = Math.max(Math.min(...values), PRICE_TICK_CANDIDATES[0]);
    const maxValue = Math.max(...values);
    const paddedMin = minValue * 0.82;
    const paddedMax = maxValue * 1.18;

    let startIndex = PRICE_TICK_CANDIDATES.findIndex((tick) => tick >= paddedMin);
    if (startIndex === -1) {
      startIndex = PRICE_TICK_CANDIDATES.length - 1;
    } else if (startIndex > 0 && PRICE_TICK_CANDIDATES[startIndex] > paddedMin) {
      startIndex -= 1;
    }

    let endIndex = PRICE_TICK_CANDIDATES.findIndex((tick) => tick >= paddedMax);
    if (endIndex === -1) {
      endIndex = PRICE_TICK_CANDIDATES.length - 1;
    }

    let ticks = PRICE_TICK_CANDIDATES.slice(startIndex, endIndex + 1);
    while (ticks.length > 4) {
      ticks = ticks.filter((_, index) => index === 0 || index === ticks.length - 1 || index % 2 === 0);
    }

    if (ticks.length < 3) {
      ticks = [PRICE_TICK_CANDIDATES[startIndex], PRICE_TICK_CANDIDATES[endIndex]].filter(Boolean);
    }

    return {
      min: ticks[0],
      max: ticks[ticks.length - 1],
      ticks,
    };
  }

  function pickInventoryScale(values) {
    if (!values.length) {
      return { max: 1000, ticks: [0, 250, 500, 750, 1000] };
    }

    const maxValue = Math.max(...values, 1);
    const targetStep = maxValue / 4;
    const step = INVENTORY_STEP_CANDIDATES.find((candidate) => candidate >= targetStep)
      || INVENTORY_STEP_CANDIDATES[INVENTORY_STEP_CANDIDATES.length - 1];
    const max = step * Math.ceil(maxValue / step);
    const ticks = [];

    for (let value = 0; value <= max; value += step) {
      ticks.push(value);
    }

    return { max, ticks };
  }

  function linearScale(value, domainMin, domainMax, rangeMin, rangeMax) {
    if (domainMax <= domainMin) {
      return (rangeMin + rangeMax) / 2;
    }
    const ratio = (value - domainMin) / (domainMax - domainMin);
    return rangeMin + (ratio * (rangeMax - rangeMin));
  }

  function logScale(value, domainMin, domainMax, rangeMin, rangeMax) {
    const safeValue = Math.max(value, 1);
    const min = Math.max(domainMin, 1);
    const max = Math.max(domainMax, min + 1);
    const ratio = (Math.log10(safeValue) - Math.log10(min)) / (Math.log10(max) - Math.log10(min));
    return rangeMin + (ratio * (rangeMax - rangeMin));
  }

  function measureTextUnits(text) {
    return normalizeString(text).split('').reduce((sum, character) => (
      /[A-Za-z0-9]/.test(character) ? sum + 0.62 : sum + 1
    ), 0);
  }

  function rectsOverlap(left, right, padding) {
    return (
      left.x < right.x + right.width + padding &&
      left.x + left.width + padding > right.x &&
      left.y < right.y + right.height + padding &&
      left.y + left.height + padding > right.y
    );
  }

  function buildLabelCandidates(point, isSelected) {
    const secondary = isSelected ? `${formatInteger(point.inventory)}本 / ${formatPriceRange(point)}` : '';
    const lineWidths = isSelected
      ? [
        22 + (measureTextUnits(point.name) * 8.4),
        22 + (measureTextUnits(secondary) * 6.7),
      ]
      : [18 + (measureTextUnits(point.shortLabel || point.name) * 7.1)];
    const width = clamp(Math.max(...lineWidths), 74, isSelected ? 200 : 128);
    const height = isSelected ? 48 : 26;
    const gap = isSelected ? 10 : 8;

    return [
      { key: 'rt', x: point.x + point.r + gap, y: point.y - height - 8, width, height, align: 'start', secondary },
      { key: 'lt', x: point.x - point.r - gap - width, y: point.y - height - 8, width, height, align: 'start', secondary },
      { key: 'rb', x: point.x + point.r + gap, y: point.y + 8, width, height, align: 'start', secondary },
      { key: 'lb', x: point.x - point.r - gap - width, y: point.y + 8, width, height, align: 'start', secondary },
    ];
  }

  function selectLabels(points, bounds, selectedModelId) {
    const results = [];
    const reservedRects = [
      { x: 0, y: 0, width: 118, height: 32 },
      { x: bounds.width - 122, y: bounds.height - 26, width: 122, height: 26 },
    ];

    const candidates = [];
    const selectedPoint = points.find((point) => point.id === selectedModelId);
    if (selectedPoint) {
      candidates.push({ point: selectedPoint, isSelected: true });
    }

    points
      .filter((point) => point.id !== selectedModelId)
      .sort((left, right) => right.inventory - left.inventory)
      .slice(0, 6)
      .forEach((point) => candidates.push({ point, isSelected: false }));

    for (const candidate of candidates) {
      const rects = buildLabelCandidates(candidate.point, candidate.isSelected);
      const placedRect = rects.find((rect) => {
        const withinBounds =
          rect.x >= 8 &&
          rect.y >= 10 &&
          rect.x + rect.width <= bounds.width - 8 &&
          rect.y + rect.height <= bounds.height - 10;
        if (!withinBounds) {
          return false;
        }

        if (reservedRects.some((reservedRect) => rectsOverlap(rect, reservedRect, 4))) {
          return false;
        }

        if (results.some((existing) => rectsOverlap(rect, existing, 8))) {
          return false;
        }

        return true;
      });

      if (!placedRect) {
        continue;
      }

      results.push({
        ...placedRect,
        modelId: candidate.point.id,
        name: candidate.point.name,
        shortLabel: candidate.point.shortLabel || candidate.point.name,
        isSelected: candidate.isSelected,
      });

      if (results.filter((entry) => !entry.isSelected).length >= 3) {
        break;
      }
    }

    return results;
  }

  function computeLayout(models, width, height) {
    const priceScale = pickPriceScale(models.map((model) => model.centralPrice).filter(Number.isFinite));
    const inventoryScale = pickInventoryScale(models.map((model) => model.inventory).filter(Number.isFinite));
    const maxInventory = Math.max(...models.map((model) => model.inventory), 1);
    const plot = {
      x: 40,
      y: 34,
      width: Math.max(width - 58, 220),
      height: Math.max(height - 88, 220),
    };

    const points = models.map((model) => {
      const x = logScale(model.centralPrice, priceScale.min, priceScale.max, plot.x + 14, plot.x + plot.width - 18);
      const y = linearScale(model.inventory, 0, inventoryScale.max, plot.y + plot.height - 12, plot.y + 12);
      const radius = clamp(10 + (Math.sqrt(model.inventory / maxInventory) * 22), 9, 27);
      const hitRadius = Math.max(radius + 10, 24);
      return {
        ...model,
        x,
        y,
        r: radius,
        hitR: hitRadius,
      };
    });

    return {
      width,
      height,
      plot,
      priceScale,
      inventoryScale,
      points,
    };
  }

  function buildSvgMarkup(layout, selectedModelId) {
    const selectedLabels = selectLabels(layout.points, layout, selectedModelId);
    const labelMap = new Map(selectedLabels.map((label) => [label.modelId, label]));

    const gridLines = layout.inventoryScale.ticks.map((tick) => {
      const y = linearScale(tick, 0, layout.inventoryScale.max, layout.plot.y + layout.plot.height - 12, layout.plot.y + 12);
      return [
        `<line x1="${layout.plot.x}" y1="${y}" x2="${layout.plot.x + layout.plot.width}" y2="${y}" stroke="#ebe3d6" stroke-width="1" />`,
        `<text x="${layout.plot.x - 8}" y="${y + 4}" font-size="10.5" font-weight="600" fill="#7a746c" text-anchor="end" font-family="Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Hiragino Sans', 'Yu Gothic', sans-serif">${escapeHtml(formatInteger(tick))}</text>`,
      ].join('');
    }).join('');

    const priceLines = layout.priceScale.ticks.map((tick, index) => {
      const x = logScale(tick, layout.priceScale.min, layout.priceScale.max, layout.plot.x + 14, layout.plot.x + layout.plot.width - 18);
      const anchor = index === 0 ? 'start' : index === layout.priceScale.ticks.length - 1 ? 'end' : 'middle';
      return [
        `<line x1="${x}" y1="${layout.plot.y}" x2="${x}" y2="${layout.plot.y + layout.plot.height}" stroke="#ebe3d6" stroke-width="1" />`,
        `<text x="${x}" y="${layout.height - 30}" font-size="10.5" font-weight="600" fill="#7a746c" text-anchor="${anchor}" font-family="Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Hiragino Sans', 'Yu Gothic', sans-serif">${escapeHtml(formatAxisTickYen(tick))}</text>`,
      ].join('');
    }).join('');

    const bubbles = layout.points.map((point) => {
      const selected = point.id === selectedModelId;
      const label = labelMap.get(point.id);
      const secondary = `${formatInteger(point.inventory)}本 / ${formatPriceRange(point)}`;

      return [
        selected
          ? `<circle cx="${point.x}" cy="${point.y}" r="${point.r + 6}" fill="none" stroke="#1f2329" stroke-width="2.2" opacity="0.95" />`
          : '',
        `<circle cx="${point.x}" cy="${point.y}" r="${selected ? point.r + 1.8 : point.r}" fill="${point.color}" fill-opacity="${selected ? '1' : '0.94'}" stroke="${selected ? '#1f2329' : '#6f7284'}" stroke-width="${selected ? '2.8' : '2'}" />`,
        label
          ? [
            `<g>`,
            `<rect x="${label.x}" y="${label.y}" width="${label.width}" height="${label.height}" fill="${label.isSelected ? '#fffdf9' : 'rgba(255,253,249,0.96)'}" stroke="${label.isSelected ? '#1f2329' : '#e5ddd2'}" />`,
            `<text x="${label.x + 10}" y="${label.y + (label.isSelected ? 16 : 16)}" font-size="${label.isSelected ? '13' : '12'}" font-weight="700" fill="#1f2329" font-family="Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Hiragino Sans', 'Yu Gothic', sans-serif">${escapeHtml(label.isSelected ? point.name : label.shortLabel)}</text>`,
            label.isSelected
              ? `<text x="${label.x + 10}" y="${label.y + 34}" font-size="10.5" font-weight="600" fill="#6f6a63" font-family="Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Hiragino Sans', 'Yu Gothic', sans-serif">${escapeHtml(secondary)}</text>`
              : '',
            `</g>`,
          ].join('')
          : '',
      ].join('');
    }).join('');

    return [
      `<svg class="ts-brand-mobile-map__svg" viewBox="0 0 ${layout.width} ${layout.height}" xmlns="http://www.w3.org/2000/svg" role="presentation" aria-hidden="true">`,
      `<rect width="${layout.width}" height="${layout.height}" fill="#f3efe8" />`,
      `<rect x="${layout.plot.x}" y="${layout.plot.y}" width="${layout.plot.width}" height="${layout.plot.height}" fill="#fbfaf6" stroke="#ddd4c8" />`,
      gridLines,
      priceLines,
      `<line x1="${layout.plot.x}" y1="${layout.plot.y + layout.plot.height}" x2="${layout.plot.x + layout.plot.width}" y2="${layout.plot.y + layout.plot.height}" stroke="#bfb4a6" stroke-width="1.2" />`,
      `<line x1="${layout.plot.x}" y1="${layout.plot.y}" x2="${layout.plot.x}" y2="${layout.plot.y + layout.plot.height}" stroke="#bfb4a6" stroke-width="1.2" />`,
      `<text x="16" y="20" font-size="11" font-weight="700" fill="#6f6a63" font-family="Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Hiragino Sans', 'Yu Gothic', sans-serif">在庫 ↑</text>`,
      `<text x="${layout.width - 16}" y="${layout.height - 8}" font-size="11" font-weight="700" fill="#6f6a63" text-anchor="end" font-family="Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Hiragino Sans', 'Yu Gothic', sans-serif">価格 →</text>`,
      bubbles,
      `</svg>`,
    ].join('');
  }

  function buildButtonMarkup(layout, selectedModelId) {
    return layout.points.map((point) => {
      const size = point.hitR * 2;
      const selected = point.id === selectedModelId;
      const label = `${point.name}。総合在庫 ${formatInteger(point.inventory)}本。参考価格 ${formatPriceRange(point)}。`;
      return [
        `<button type="button" class="ts-brand-mobile-map__bubble-target${selected ? ' is-selected' : ''}"`,
        ` data-map-bubble-button`,
        ` data-model-id="${escapeHtml(point.id)}"`,
        ` aria-label="${escapeHtml(label)}"`,
        ` aria-pressed="${selected ? 'true' : 'false'}"`,
        ` style="left:${point.x - point.hitR}px;top:${point.y - point.hitR}px;width:${size}px;height:${size}px;"`,
        `>`,
        `<span class="visually-hidden">${escapeHtml(point.name)}</span>`,
        `</button>`,
      ].join('');
    }).join('');
  }

  class BrandMobileMap {
    constructor(root) {
      this.root = root;
      this.brandHandle = normalizeString(root.dataset.brandHandle);
      this.brandName = normalizeString(root.dataset.brandName) || this.brandHandle.replace(/^brand-/, '');
      const rawModels = parseJson(root.querySelector('[data-ts-brand-mobile-map-data]')?.textContent || '[]', []);
      const marketItemsNode = Array.from(document.querySelectorAll(MARKET_ITEMS_SELECTOR)).find((node) => {
        const nodeBrandHandle = normalizeString(node.dataset.brandHandle);
        return !nodeBrandHandle || nodeBrandHandle === this.brandHandle;
      });
      const marketItems = dedupeMarketItems([
        ...parseJson(marketItemsNode?.textContent || '[]', []),
        ...collectMarketItemsFromDom(),
      ]);
      const modelsFromPayload = ensureArray(rawModels)
        .map((model, index) => normalizeModel(model, this.brandHandle, index))
        .filter(Boolean);
      const modelsFromMarket = deriveMarketModels(rawModels, marketItems, this.brandHandle);
      const initialModels = mergeModels(modelsFromPayload, modelsFromMarket);
      hydratePopularCards(this.brandHandle, initialModels);
      const modelsFromCards = collectCardModels(this.brandHandle);
      this.models = mergeModels(initialModels, modelsFromCards);

      this.viewport = root.querySelector('[data-ts-brand-mobile-map-viewport]');
      this.canvas = root.querySelector('[data-ts-brand-mobile-map-canvas]');
      this.stage = root.querySelector('[data-ts-brand-mobile-map-stage]');
      this.chips = root.querySelector('[data-ts-brand-mobile-map-chips]');

      this.state = {
        selectedModelId: '',
        activeSource: 'map',
      };

      this.layout = null;
      this.transform = { scale: 1, x: 0, y: 0 };
      this.boundCards = new Set();
      this.resizeRaf = 0;

      if (!this.viewport || !this.canvas || !this.stage || this.models.length === 0) {
        this.root.hidden = true;
        return;
      }

      this.bind();
      this.refreshLinkedCards();
      this.render();
    }

    bind() {
      this.stage.addEventListener('click', (event) => {
        if (!isMobileViewport()) {
          return;
        }

        const bubbleButton = event.target.closest('[data-map-bubble-button]');
        if (!bubbleButton) {
          return;
        }

        event.preventDefault();
        const modelId = bubbleButton.getAttribute('data-model-id');
        const nearestModelId = this.findNearestModelId(event.clientX, event.clientY) || modelId;
        this.handleSelectionIntent(nearestModelId, 'map');
      });

      this.stage.addEventListener('keydown', (event) => {
        const bubbleButton = event.target.closest('[data-map-bubble-button]');
        if (!bubbleButton) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.handleSelectionIntent(bubbleButton.getAttribute('data-model-id'), 'map');
        }
      });

      this.chips?.addEventListener('click', (event) => {
        const chipButton = event.target.closest('[data-model-id]');
        if (!chipButton) {
          return;
        }
        event.preventDefault();
        this.handleSelectionIntent(chipButton.getAttribute('data-model-id'), 'chip');
      });

      const rerender = () => {
        if (this.resizeRaf) {
          window.cancelAnimationFrame(this.resizeRaf);
        }

        this.resizeRaf = window.requestAnimationFrame(() => {
          this.resizeRaf = 0;
          this.render();
        });
      };

      window.addEventListener('resize', rerender, { passive: true });

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(rerender);
        this.resizeObserver.observe(this.canvas);
      }
    }

    selectModel(modelId, source) {
      const nextModel = this.models.find((model) => model.id === modelId);
      if (!nextModel) {
        return;
      }

      this.state.selectedModelId = nextModel.id;
      this.state.activeSource = source || 'map';

      this.render();

      pushMetric('brand_map_model_selected', {
        brand: this.brandHandle.replace(/^brand-/, ''),
        modelId: nextModel.id,
        source: this.state.activeSource,
      });

      if (this.state.activeSource === 'chip') {
        pushMetric('brand_map_chip_selected', {
          brand: this.brandHandle.replace(/^brand-/, ''),
          modelId: nextModel.id,
        });
      }

      if (this.state.activeSource === 'list') {
        pushMetric('brand_map_list_selected', {
          brand: this.brandHandle.replace(/^brand-/, ''),
          modelId: nextModel.id,
        });
      }
    }

    navigateToModel(model, source) {
      if (!model || !model.href || !model.available) {
        return;
      }

      pushMetric('brand_map_inventory_cta_clicked', {
        brand: this.brandHandle.replace(/^brand-/, ''),
        modelId: model.id,
        source: source || this.state.activeSource || 'map',
      });
      window.location.assign(model.href);
    }

    handleSelectionIntent(modelId, source) {
      const nextModel = this.models.find((model) => model.id === modelId);
      if (!nextModel) {
        return;
      }

      if (this.state.selectedModelId === nextModel.id) {
        this.navigateToModel(nextModel, source);
        return;
      }

      this.selectModel(nextModel.id, source);
    }

    scrollActiveChipIntoView() {
      const activeChip = this.chips?.querySelector('.is-active');
      if (!activeChip || typeof activeChip.scrollIntoView !== 'function') {
        return;
      }

      activeChip.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }

    refreshLinkedCards() {
      const cards = Array.from(document.querySelectorAll(LIST_CARD_SELECTOR))
        .filter((card) => normalizeString(card.dataset.brandHandle) === this.brandHandle);

      cards.forEach((card) => {
        if (this.boundCards.has(card)) {
          return;
        }

        this.boundCards.add(card);
        card.addEventListener('click', (event) => {
          if (!isMobileViewport()) {
            return;
          }

          const modelId = normalizeString(card.dataset.modelId);
          if (!modelId) {
            return;
          }

          const model = this.models.find((entry) => entry.id === modelId);
          const modelUrl = normalizeString(card.dataset.modelUrl || card.getAttribute('href') || model?.href);
          if (modelUrl) {
            event.preventDefault();
            pushMetric('brand_map_inventory_cta_clicked', {
              brand: this.brandHandle.replace(/^brand-/, ''),
              modelId,
              source: 'list',
            });
            window.location.assign(modelUrl);
            return;
          }

          event.preventDefault();
          this.selectModel(modelId, 'list');
        });
      });

      this.cards = cards;
    }

    syncLinkedCards() {
      const selectedModelId = this.state.selectedModelId;
      this.refreshLinkedCards();

      this.cards.forEach((card) => {
        const isActive = selectedModelId && normalizeString(card.dataset.modelId) === selectedModelId;
        card.classList.toggle('is-brand-map-active', Boolean(isActive));
        if (isActive) {
          card.setAttribute('aria-current', 'true');
        } else {
          card.removeAttribute('aria-current');
        }
      });
    }

    findNearestModelId(clientX, clientY) {
      if (!this.layout) {
        return '';
      }

      const rect = this.canvas.getBoundingClientRect();
      const x = (clientX - rect.left - this.transform.x) / this.transform.scale;
      const y = (clientY - rect.top - this.transform.y) / this.transform.scale;

      let bestPoint = null;
      let bestScore = Infinity;
      for (const point of this.layout.points) {
        const distance = Math.hypot(point.x - x, point.y - y);
        const threshold = Math.max(point.hitR, 24);
        const score = distance / threshold;
        if (score <= 1.35 && score < bestScore) {
          bestPoint = point;
          bestScore = score;
        }
      }

      return bestPoint ? bestPoint.id : '';
    }

    renderChips() {
      if (!this.chips) {
        return;
      }

      this.chips.innerHTML = this.models.map((model) => (
        `<button type="button" class="ts-brand-mobile-map__chip${model.id === this.state.selectedModelId ? ' is-active' : ''}" data-model-id="${escapeHtml(model.id)}" aria-pressed="${model.id === this.state.selectedModelId ? 'true' : 'false'}">${escapeHtml(model.shortLabel || model.name)}</button>`
      )).join('');

      if (this.state.selectedModelId) {
        this.scrollActiveChipIntoView();
      }
    }

    renderScene() {
      const width = Math.max(Math.round(this.canvas.clientWidth || this.root.clientWidth || 320), 320);
      const height = Math.max(Math.round(this.canvas.clientHeight || 460), 420);
      this.layout = computeLayout(this.models, width, height);
      this.transform = { scale: 1, x: 0, y: 0 };

      this.stage.innerHTML = [
        `<div class="ts-brand-mobile-map__scene">`,
        buildSvgMarkup(this.layout, this.state.selectedModelId),
        buildButtonMarkup(this.layout, this.state.selectedModelId),
        `</div>`,
      ].join('');
    }

    render() {
      this.root.dataset.hasSelection = this.state.selectedModelId ? 'true' : 'false';
      this.renderScene();
      this.renderChips();
      this.syncLinkedCards();
    }
  }

  function boot(scope) {
    const roots = Array.from((scope || document).querySelectorAll(ROOT_SELECTOR));
    roots.forEach((root) => {
      if (root.__tsBrandMobileMap) {
        root.__tsBrandMobileMap.refreshLinkedCards();
        root.__tsBrandMobileMap.render();
        return;
      }

      const controller = new BrandMobileMap(root);
      root.__tsBrandMobileMap = controller;
      if (controller.brandHandle) {
        controllers.set(controller.brandHandle, controller);
      }
    });

    controllers.forEach((controller) => controller.refreshLinkedCards());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
  });
})();
